import * as Yup from 'yup';
import moment from 'moment-timezone';

import { PaginationRequestParams } from '~globals/types/api';
import { Nullable } from '~globals/types/commons';
import { ScheduleRouteRepetitionType } from '~globals/types/enums';
import { VALIDATIONS_ERROR } from '~constants/validationsError';
import {
  ScheduleRouteExtended,
  ScheduleRouteWeeklyDayIndex,
  ScheduleRouteUpdateBody,
} from '~services/scheduleRoute/types';
import { DriverCompanyBase } from '~services/driver/types';
import { VehicleSearchItem } from '~services/vehicle/types';
import { VehicleFilter } from '~services/vehicle/types';
import { FormatterDateUtcOffset } from '~hooks/useUtcOffset';
// eslint-disable-next-line max-len
import {
  normalizeAndSortWeeklyDays,
  sortWeeklyDays,
  transformRepetitionIntervalStringToArray,
} from '~utils/scheduleRoute/repetionDescription/helpers';
import { isRouteScheduleRepetionTypeWeekly } from '~utils/scheduleRoute/repetionType';
import { formatterDate } from '~utils/formatter';

export const FIELDS_NAME = {
  ENABLED: 'enabled',
  TITLE: 'title',
  DRIVER: 'driver',
  VEHICLE: 'vehicle',
  END_DATE: 'endDate',
  REPETION_TYPE: 'repetionType',
  REPETION_INTERVAL_COUNT: 'repetionIntervalCount',
  REPETION_INTERVAL_DAYS: 'repetionIntervalDays',
} as const;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getInitialValues = (data: ScheduleRouteExtended) => ({
  [FIELDS_NAME.ENABLED]: data.enabled ?? true,
  [FIELDS_NAME.TITLE]: data.title ?? '',
  [FIELDS_NAME.DRIVER]: data.driverId ?? '',
  [FIELDS_NAME.VEHICLE]: data.vehicleId ?? '',
  [FIELDS_NAME.END_DATE]: data.endDate ? moment(data.endDate).toDate() : null,
  [FIELDS_NAME.REPETION_TYPE]:
    data.repetitionTypeId ?? ScheduleRouteRepetitionType.Weekly,
  [FIELDS_NAME.REPETION_INTERVAL_COUNT]: data.repeatInterval ?? 1,
  [FIELDS_NAME.REPETION_INTERVAL_DAYS]: (data.repetitionIntervalArray
    ? normalizeAndSortWeeklyDays(
        transformRepetitionIntervalStringToArray(data.repetitionIntervalArray),
      )
    : []) as ScheduleRouteWeeklyDayIndex[],
});

export const validationSchema = Yup.object().shape({
  [FIELDS_NAME.ENABLED]: Yup.boolean(),
  [FIELDS_NAME.TITLE]: Yup.string().required(VALIDATIONS_ERROR.REQUIRED),
  [FIELDS_NAME.DRIVER]: Yup.string().notRequired(),
  [FIELDS_NAME.VEHICLE]: Yup.string().required(VALIDATIONS_ERROR.REQUIRED),
  [FIELDS_NAME.END_DATE]: Yup.date()
    .nullable()
    .typeError(VALIDATIONS_ERROR.INVALID_DATE)
    .required(VALIDATIONS_ERROR.REQUIRED),
  [FIELDS_NAME.REPETION_TYPE]: Yup.number().required(
    VALIDATIONS_ERROR.REQUIRED,
  ),
  [FIELDS_NAME.REPETION_INTERVAL_COUNT]: Yup.number()
    .moreThan(0, VALIDATIONS_ERROR.MORE_THAN(0))
    .required(VALIDATIONS_ERROR.REQUIRED),
  [FIELDS_NAME.REPETION_INTERVAL_DAYS]: Yup.array()
    .of(Yup.number())
    .when(FIELDS_NAME.REPETION_TYPE, {
      is: (scheduleRouteRepetionType: ScheduleRouteRepetitionType) =>
        isRouteScheduleRepetionTypeWeekly(scheduleRouteRepetionType),
      then: (schema) => schema.min(1, 'Debe seleccionar al menos uno'),
    }),
});

const getVehicleRequestDateTime = (
  apiTransactionScheduledDateTime: string,
): string => {
  const currentDate = formatterDate(moment(), {
    format: 'YYYY-MM-DD',
    parseToUtc: false,
  });

  const currentTime = formatterDate(apiTransactionScheduledDateTime, {
    format: 'HH:mm:ss.SSS',
    parseToUtc: false,
  });

  return `${currentDate}T${currentTime}`;
};

export const getDefaultVehiclesRequestPayload = (
  apiTransactionScheduledDateTime: string,
): PaginationRequestParams<VehicleFilter> => ({
  pagination: {
    page: 1,
    sizePerPage: -1,
  },
  filters: {
    enabled: null,
    query: null,
    scheduleDateTime: getVehicleRequestDateTime(
      apiTransactionScheduledDateTime,
    ),
  },
});

export const getLabelDriverOption = (option: DriverCompanyBase): string =>
  option.displayName;

export const getDisabledDriverOption = (
  option: DriverCompanyBase,
  defaultDriver: DriverCompanyBase | undefined,
): boolean => option.id !== defaultDriver?.id && !option.isAvailable;

export const getLabelVehicleOption = (option: VehicleSearchItem): string => {
  let label = option.name;

  if (option.referenceCode) {
    label += ` (${option.referenceCode})`;
  }

  return label;
};

export const getDisabledVehicleOption = (
  option: VehicleSearchItem,
  defaultVehicle: VehicleSearchItem | undefined,
): boolean =>
  option.vehicleId !== defaultVehicle?.vehicleId && !option.isAvailable;

export const getUpdateScheduleRouteBody = ({
  scheduleRouteId,
  templateId,
  startDate: startDateValue,
  formValues,
  formatterDateUtcOffset,
}: {
  scheduleRouteId: string;
  templateId: string;
  startDate: string;
  formValues: ReturnType<typeof getInitialValues>;
  formatterDateUtcOffset: FormatterDateUtcOffset;
}): ScheduleRouteUpdateBody => {
  const enabled = formValues[FIELDS_NAME.ENABLED];

  const title = formValues[FIELDS_NAME.TITLE];

  const startDate = formatterDateUtcOffset(
    moment(startDateValue).startOf('day'),
    moment.HTML5_FMT.DATETIME_LOCAL_MS,
    true,
  );

  const endDate = formatterDateUtcOffset(
    moment(formValues[FIELDS_NAME.END_DATE]).startOf('day'),
    moment.HTML5_FMT.DATETIME_LOCAL_MS,
    true,
  );

  const repetitionTypeId: ScheduleRouteRepetitionType = Number(
    formValues[FIELDS_NAME.REPETION_TYPE],
  );

  const repeatInterval = Number(
    formValues[FIELDS_NAME.REPETION_INTERVAL_COUNT],
  );

  let repetitionIntervalArray: Nullable<string> = null;

  if (isRouteScheduleRepetionTypeWeekly(repetitionTypeId)) {
    repetitionIntervalArray = sortWeeklyDays(
      formValues[FIELDS_NAME.REPETION_INTERVAL_DAYS],
    ).join(',');
  }

  const driverId = formValues[FIELDS_NAME.DRIVER] || null;

  const vehicleId = formValues[FIELDS_NAME.VEHICLE];

  return {
    routeScheduleId: scheduleRouteId,
    routeTemplateId: templateId,
    enabled,
    title,
    startDate,
    endDate,
    repetitionTypeId,
    repetitionIntervalArray,
    repeatInterval,
    driverId,
    vehicleId,
  };
};
